import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  VpnKey,
  CheckCircle,
  Cancel,
  Edit,
  Warning
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const LicencesManagement = () => {
  const [cabinets, setCabinets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCabinet, setSelectedCabinet] = useState(null);
  const [extensionDays, setExtensionDays] = useState(30);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({
    totalLicences: 0,
    actives: 0,
    expirees: 0,
    expirentBientot: 0
  });

  useEffect(() => {
    loadCabinets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculateStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cabinets]);

  const loadCabinets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/super-admin/cabinets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCabinets(response.data.data || []);
    } catch (error) {
      showSnackbar('Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const now = new Date();
    const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    let actives = 0;
    let expirees = 0;
    let expirentBientot = 0;

    cabinets.forEach(cabinet => {
      const licence = cabinet.Licences?.[0];
      if (licence) {
        const expiration = new Date(licence.date_expiration);
        if (licence.statut && expiration > now) {
          actives++;
          if (expiration <= in7Days) {
            expirentBientot++;
          }
        } else {
          expirees++;
        }
      }
    });

    setStats({
      totalLicences: cabinets.length,
      actives,
      expirees,
      expirentBientot
    });
  };

  const handleToggleLicence = async (cabinet) => {
    const licence = cabinet.Licences?.[0];
    if (!licence) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/super-admin/licences/${licence.id_licence}`,
        { statut: !licence.statut },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar(
        `Licence ${!licence.statut ? 'activée' : 'désactivée'} pour ${cabinet.nom_cabinet}`,
        'success'
      );
      loadCabinets();
    } catch (error) {
      showSnackbar('Erreur lors de la modification', 'error');
    }
  };

  const handleOpenExtendDialog = (cabinet) => {
    setSelectedCabinet(cabinet);
    setExtensionDays(30);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCabinet(null);
  };

  const handleExtendLicence = async () => {
    if (!selectedCabinet) return;

    try {
      const token = localStorage.getItem('token');
      const licence = selectedCabinet.Licences?.[0];
      
      if (!licence) {
        showSnackbar('Aucune licence trouvée', 'error');
        return;
      }

      const currentExpiration = new Date(licence.date_expiration);
      const newExpiration = new Date(currentExpiration.getTime() + extensionDays * 24 * 60 * 60 * 1000);

      // Créer une nouvelle licence ou mettre à jour
      // Note: Adapter selon votre API backend
      await axios.post(
        `${API_URL}/super-admin/licences`,
        {
          id_cabinet: selectedCabinet.id_cabinet,
          date_activation: new Date(),
          date_expiration: newExpiration,
          statut: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showSnackbar(`Licence prolongée de ${extensionDays} jours`, 'success');
      handleCloseDialog();
      loadCabinets();
    } catch (error) {
      showSnackbar('Erreur lors de la prolongation', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getLicenceStatus = (cabinet) => {
    const licence = cabinet.Licences?.[0];
    if (!licence) return { label: 'Aucune', color: 'default', icon: <Cancel /> };

    const now = new Date();
    const expiration = new Date(licence.date_expiration);
    const daysRemaining = Math.ceil((expiration - now) / (1000 * 60 * 60 * 24));

    if (!licence.statut) {
      return { label: 'Désactivée', color: 'error', icon: <Cancel />, days: null };
    }

    if (expiration < now) {
      return { label: 'Expirée', color: 'error', icon: <Cancel />, days: null };
    }

    if (daysRemaining <= 7) {
      return { label: 'Expire bientôt', color: 'warning', icon: <Warning />, days: daysRemaining };
    }

    return { label: 'Active', color: 'success', icon: <CheckCircle />, days: daysRemaining };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom>
        <VpnKey sx={{ mr: 1, verticalAlign: 'middle' }} />
        Gestion des Licences
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Activer, désactiver et prolonger les licences des cabinets
      </Typography>

      {/* Statistiques */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="text.secondary" variant="body2">
                    Total Licences
                  </Typography>
                  <Typography variant="h4">{stats.totalLicences}</Typography>
                </div>
                <VpnKey color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="text.secondary" variant="body2">
                    Actives
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.actives}
                  </Typography>
                </div>
                <CheckCircle color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="text.secondary" variant="body2">
                    Expirent bientôt
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.expirentBientot}
                  </Typography>
                </div>
                <Warning color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="text.secondary" variant="body2">
                    Expirées
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {stats.expirees}
                  </Typography>
                </div>
                <Cancel color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tableau des licences */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Cabinet</strong></TableCell>
              <TableCell><strong>Administrateur</strong></TableCell>
              <TableCell><strong>Date Activation</strong></TableCell>
              <TableCell><strong>Date Expiration</strong></TableCell>
              <TableCell><strong>Jours restants</strong></TableCell>
              <TableCell><strong>Statut</strong></TableCell>
              <TableCell align="center"><strong>Active/Désactive</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cabinets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary">
                    Aucun cabinet trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              cabinets.map((cabinet) => {
                const licence = cabinet.Licences?.[0];
                const admin = cabinet.Utilisateurs?.[0];
                const status = getLicenceStatus(cabinet);

                return (
                  <TableRow key={cabinet.id_cabinet} hover>
                    <TableCell>{cabinet.id_cabinet}</TableCell>
                    <TableCell>
                      <strong>{cabinet.nom_cabinet}</strong>
                    </TableCell>
                    <TableCell>
                      {admin ? `${admin.prenom_user} ${admin.nom_user}` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {licence ? new Date(licence.date_activation).toLocaleDateString('fr-FR') : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {licence ? new Date(licence.date_expiration).toLocaleDateString('fr-FR') : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {status.days !== null ? (
                        <Chip
                          label={`${status.days} jours`}
                          size="small"
                          color={status.days <= 7 ? 'warning' : 'default'}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={status.icon}
                        label={status.label}
                        color={status.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={licence?.statut || false}
                        onChange={() => handleToggleLicence(cabinet)}
                        color="success"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => handleOpenExtendDialog(cabinet)}
                      >
                        Prolonger
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de prolongation */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Prolonger la licence - {selectedCabinet?.nom_cabinet}
        </DialogTitle>
        <DialogContent>
          {selectedCabinet?.Licences?.[0] && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Expiration actuelle : {new Date(selectedCabinet.Licences[0].date_expiration).toLocaleDateString('fr-FR')}
            </Alert>
          )}
          <TextField
            fullWidth
            type="number"
            label="Nombre de jours à ajouter"
            value={extensionDays}
            onChange={(e) => setExtensionDays(parseInt(e.target.value) || 0)}
            inputProps={{ min: 1, max: 365 }}
            sx={{ mt: 2 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Nouvelle date d'expiration :{' '}
            {selectedCabinet?.Licences?.[0] && 
              new Date(
                new Date(selectedCabinet.Licences[0].date_expiration).getTime() + 
                extensionDays * 24 * 60 * 60 * 1000
              ).toLocaleDateString('fr-FR')
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button variant="contained" onClick={handleExtendLicence}>
            Prolonger
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LicencesManagement;